<template>
  <!-- ══ LOGGED OUT - split layout ══ -->
  <div v-if="!auth.isLoggedIn" class="min-h-screen flex flex-col md:flex-row">

    <!-- Left panel - brand / USPs -->
    <div class="hidden md:flex md:w-[42%] lg:w-[38%] flex-col bg-[#111] text-white relative overflow-hidden shrink-0">

      <!-- Gele accentlijn bovenaan -->
      <div class="h-1 w-full bg-gradient-to-r from-[#FFD500] via-[#FFB800] to-[#FFD500]"></div>

      <!-- Decoratieve achtergrondcirkel -->
      <div class="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-white/[0.02] pointer-events-none"></div>
      <div class="absolute top-1/2 -right-24 w-[280px] h-[280px] rounded-full bg-[#FFD500]/5 pointer-events-none"></div>

      <div class="flex-1 flex flex-col justify-between px-10 py-12 relative z-10">
        <!-- Logo + tagline -->
        <div>
          <router-link to="/" class="no-underline inline-block mb-10">
            <Logo :height="38" :inverted="true" />
          </router-link>

          <h2 class="text-[1.6rem] font-bold leading-tight mb-3 text-white">
            Jouw account voor<br>slimme laadoplossingen
          </h2>
          <p class="text-white/50 text-[0.9rem] leading-relaxed mb-10">
            Beheer bestellingen, volg leveringen en krijg als installateur toegang tot exclusieve prijzen.
          </p>

          <!-- USPs -->
          <ul class="list-none p-0 m-0 flex flex-col gap-5">
            <li v-for="usp in usps" :key="usp.text" class="flex items-start gap-4">
              <span class="w-9 h-9 rounded-lg bg-[#FFD500]/10 flex items-center justify-center shrink-0 mt-0.5">
                <i :class="['bi text-[#FFD500] text-[1rem]', usp.icon]"></i>
              </span>
              <div>
                <p class="text-[0.88rem] font-semibold text-white leading-snug">{{ usp.title }}</p>
                <p class="text-[0.78rem] text-white/45 mt-0.5 leading-snug">{{ usp.sub }}</p>
              </div>
            </li>
          </ul>
        </div>

        <!-- Footer tekst -->
        <p class="text-[0.75rem] text-white/25 mt-12">
          &copy; {{ new Date().getFullYear() }} Plug2Charge.nl · Eerlijk geprijsd, snel geleverd.
        </p>
      </div>
    </div>

    <!-- Right panel - form -->
    <div class="flex-1 flex flex-col items-center justify-center bg-[#f6f7f9] px-6 py-12">

      <!-- Mobiel logo -->
      <div class="md:hidden mb-8">
        <router-link to="/" class="no-underline inline-block">
          <Logo :height="36" />
        </router-link>
      </div>

      <div class="w-full max-w-[420px]">

        <!-- ── FORGOT PASSWORD VIEW ── -->
        <div v-if="view === 'forgot'">
          <button @click="view = 'login'" class="flex items-center gap-1.5 text-[0.82rem] text-[#888] hover:text-[#111] mb-6 transition-colors">
            <i class="bi bi-arrow-left text-[0.75rem]"></i> Terug naar inloggen
          </button>

          <div v-if="!forgotSent" class="bg-white rounded-2xl shadow-sm border border-[#e9eaec] p-8">
            <div class="w-12 h-12 rounded-xl bg-[#fffbe6] flex items-center justify-center mb-5">
              <i class="bi bi-key text-[#FFD500] text-[1.3rem]"></i>
            </div>
            <h1 class="text-[1.3rem] font-bold text-[#111] mb-1">Wachtwoord vergeten?</h1>
            <p class="text-[0.875rem] text-[#888] mb-7">Vul je e-mailadres in en we sturen je een resetlink.</p>

            <form @submit.prevent="handleForgotPassword" class="flex flex-col gap-5">
              <div>
                <label class="block text-[0.82rem] font-semibold text-[#444] mb-1.5">E-mailadres</label>
                <div class="relative">
                  <i class="bi bi-envelope absolute left-3.5 top-1/2 -translate-y-1/2 text-[#bbb] text-[0.95rem]"></i>
                  <input v-model="forgotEmail" type="email" required placeholder="jouw@email.nl"
                    class="w-full pl-10 pr-4 py-2.5 border border-[#e5e7eb] rounded-lg text-[0.9rem] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-colors">
                </div>
              </div>

              <div v-if="forgotError" class="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-[0.85rem]">
                <i class="bi bi-exclamation-triangle shrink-0"></i> {{ forgotError }}
              </div>

              <button type="submit" :disabled="forgotLoading"
                class="w-full bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold py-3 rounded-lg acct-btn flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                <i :class="['bi', forgotLoading ? 'bi-arrow-repeat spin' : 'bi-send']"></i>
                {{ forgotLoading ? 'Versturen...' : 'Resetlink versturen' }}
              </button>
            </form>
          </div>

          <!-- Succes-state -->
          <div v-else class="bg-white rounded-2xl shadow-sm border border-[#e9eaec] p-8 text-center">
            <div class="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
              <i class="bi bi-envelope-check text-green-600 text-[1.6rem]"></i>
            </div>
            <h1 class="text-[1.2rem] font-bold text-[#111] mb-2">Mail verstuurd!</h1>
            <p class="text-[0.875rem] text-[#888] mb-7">
              Controleer je inbox op <strong class="text-[#111]">{{ forgotEmail }}</strong>. De link is 24 uur geldig.
            </p>
            <button @click="view = 'login'; forgotSent = false; forgotEmail = ''"
              class="w-full border border-[#e5e7eb] text-[#555] hover:border-[#FFD500] hover:text-[#111] py-2.5 rounded-lg text-[0.875rem] font-semibold transition-colors">
              Terug naar inloggen
            </button>
          </div>
        </div>

        <!-- ── LOGIN / REGISTER VIEWS ── -->
        <div v-else>
          <!-- Tabs -->
          <div class="flex rounded-xl overflow-hidden border border-[#e5e7eb] mb-6 bg-white">
            <button
              @click="view = 'login'"
              :class="['flex-1 py-3 text-[0.9rem] font-semibold transition-colors', view === 'login' ? 'bg-[#FFD500] text-[#111]' : 'bg-white text-[#666] hover:text-[#111]']"
            >Inloggen</button>
            <button
              @click="view = 'register'"
              :class="['flex-1 py-3 text-[0.9rem] font-semibold transition-colors', view === 'register' ? 'bg-[#FFD500] text-[#111]' : 'bg-white text-[#666] hover:text-[#111]']"
            >Account aanmaken</button>
          </div>

          <!-- Login Form -->
          <div v-if="view === 'login'" class="bg-white rounded-2xl shadow-sm border border-[#e9eaec] p-8">
            <h1 class="text-[1.4rem] font-bold text-[#111] mb-1">Welkom terug</h1>
            <p class="text-[0.875rem] text-[#888] mb-7">Log in op je Plug2Charge account</p>

            <form @submit.prevent="handleLogin" class="flex flex-col gap-5">
              <div>
                <label class="block text-[0.82rem] font-semibold text-[#444] mb-1.5">E-mailadres</label>
                <div class="relative">
                  <i class="bi bi-envelope absolute left-3.5 top-1/2 -translate-y-1/2 text-[#bbb] text-[0.95rem]"></i>
                  <input v-model="loginForm.email" type="email" required autocomplete="email" placeholder="jouw@email.nl"
                    class="w-full pl-10 pr-4 py-2.5 border border-[#e5e7eb] rounded-lg text-[0.9rem] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-colors">
                </div>
              </div>

              <div>
                <div class="flex justify-between mb-1.5">
                  <label class="text-[0.82rem] font-semibold text-[#444]">Wachtwoord</label>
                  <button type="button" @click="view = 'forgot'" class="text-[0.78rem] text-[#888] hover:text-[#FFD500] font-medium transition-colors">
                    Vergeten?
                  </button>
                </div>
                <div class="relative">
                  <i class="bi bi-lock absolute left-3.5 top-1/2 -translate-y-1/2 text-[#bbb] text-[0.95rem]"></i>
                  <input v-model="loginForm.password" :type="showPassword ? 'text' : 'password'" required autocomplete="current-password" placeholder="••••••••"
                    class="w-full pl-10 pr-10 py-2.5 border border-[#e5e7eb] rounded-lg text-[0.9rem] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-colors">
                  <button type="button" @click="showPassword = !showPassword"
                    :aria-label="showPassword ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'"
                    class="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#bbb] hover:text-[#666] transition-colors">
                    <i :class="['bi', showPassword ? 'bi-eye-slash' : 'bi-eye']" aria-hidden="true"></i>
                  </button>
                </div>
              </div>

              <label class="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" v-model="loginForm.remember" class="w-4 h-4 accent-[#FFD500]">
                <span class="text-[0.85rem] text-[#666]">Onthoud mij</span>
              </label>

              <div v-if="loginError" class="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-[0.85rem]">
                <i class="bi bi-exclamation-triangle shrink-0"></i>
                {{ loginError }}
              </div>

              <button type="submit" :disabled="auth.loading"
                class="w-full bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold py-3 rounded-lg acct-btn flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                <i :class="['bi', auth.loading ? 'bi-arrow-repeat spin' : 'bi-arrow-right']"></i>
                {{ auth.loading ? 'Inloggen...' : 'Inloggen' }}
              </button>
            </form>

            <p class="text-center text-[0.82rem] text-[#aaa] mt-6">
              Nog geen account?
              <button @click="view = 'register'" class="text-[#111] font-semibold hover:underline">Maak er gratis een aan</button>
            </p>
          </div>

          <!-- Register Form -->
          <div v-if="view === 'register'" class="bg-white rounded-2xl shadow-sm border border-[#e9eaec] p-8">
            <h1 class="text-[1.4rem] font-bold text-[#111] mb-1">Account aanmaken</h1>
            <p class="text-[0.875rem] text-[#888] mb-7">Gratis en altijd je bestellingen bij de hand</p>

            <form @submit.prevent="handleRegister" class="flex flex-col gap-5">
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-[0.82rem] font-semibold text-[#444] mb-1.5">Voornaam</label>
                  <input v-model="registerForm.firstName" type="text" required placeholder="Jan"
                    class="w-full px-4 py-2.5 border border-[#e5e7eb] rounded-lg text-[0.9rem] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-colors">
                </div>
                <div>
                  <label class="block text-[0.82rem] font-semibold text-[#444] mb-1.5">Achternaam</label>
                  <input v-model="registerForm.lastName" type="text" placeholder="Janssen"
                    class="w-full px-4 py-2.5 border border-[#e5e7eb] rounded-lg text-[0.9rem] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-colors">
                </div>
              </div>

              <div>
                <label class="block text-[0.82rem] font-semibold text-[#444] mb-1.5">E-mailadres</label>
                <div class="relative">
                  <i class="bi bi-envelope absolute left-3.5 top-1/2 -translate-y-1/2 text-[#bbb] text-[0.95rem]"></i>
                  <input v-model="registerForm.email" type="email" required placeholder="jouw@email.nl"
                    class="w-full pl-10 pr-4 py-2.5 border border-[#e5e7eb] rounded-lg text-[0.9rem] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-colors">
                </div>
              </div>

              <div>
                <label class="block text-[0.82rem] font-semibold text-[#444] mb-1.5">Wachtwoord</label>
                <div class="relative">
                  <i class="bi bi-lock absolute left-3.5 top-1/2 -translate-y-1/2 text-[#bbb] text-[0.95rem]"></i>
                  <input v-model="registerForm.password" :type="showRegPassword ? 'text' : 'password'" required minlength="8" placeholder="Minimaal 8 tekens"
                    class="w-full pl-10 pr-10 py-2.5 border border-[#e5e7eb] rounded-lg text-[0.9rem] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-colors">
                  <button type="button" @click="showRegPassword = !showRegPassword"
                    :aria-label="showRegPassword ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'"
                    class="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#bbb] hover:text-[#666] transition-colors">
                    <i :class="['bi', showRegPassword ? 'bi-eye-slash' : 'bi-eye']" aria-hidden="true"></i>
                  </button>
                </div>
                <!-- Sterkte meter -->
                <div v-if="registerForm.password" class="mt-2">
                  <div class="flex items-center gap-1.5 mb-1.5">
                    <div v-for="i in 4" :key="i"
                      :class="['h-1 flex-1 rounded-full transition-colors duration-300', i <= passwordStrength ? strengthColor : 'bg-[#e5e7eb]']"></div>
                    <span class="text-[0.72rem] text-[#888] ml-1 w-8">{{ strengthLabel }}</span>
                  </div>
                  <!-- Vereisten -->
                  <ul class="list-none p-0 m-0 grid grid-cols-2 gap-y-0.5">
                    <li v-for="req in pwRequirements" :key="req.label"
                      :class="['flex items-center gap-1.5 text-[0.72rem] transition-colors', req.met ? 'text-green-600' : 'text-[#bbb]']">
                      <i :class="['bi text-[0.65rem]', req.met ? 'bi-check-circle-fill' : 'bi-circle']"></i>
                      {{ req.label }}
                    </li>
                  </ul>
                </div>
              </div>

              <div v-if="registerError" class="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-[0.85rem]">
                <i class="bi bi-exclamation-triangle shrink-0"></i>
                {{ registerError }}
              </div>

              <button type="submit" :disabled="auth.loading"
                class="w-full bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold py-3 rounded-lg acct-btn flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                <i :class="['bi', auth.loading ? 'bi-arrow-repeat spin' : 'bi-person-check']"></i>
                {{ auth.loading ? 'Account aanmaken...' : 'Account aanmaken' }}
              </button>

              <p class="text-[0.75rem] text-[#aaa] text-center leading-relaxed">
                Door een account aan te maken ga je akkoord met onze
                <router-link to="/algemene-voorwaarden" class="text-[#666] underline">Algemene Voorwaarden</router-link>
                en
                <router-link to="/privacy" class="text-[#666] underline">Privacybeleid</router-link>.
              </p>
            </form>

            <p class="text-center text-[0.82rem] text-[#aaa] mt-6">
              Al een account?
              <button @click="view = 'login'" class="text-[#111] font-semibold hover:underline">Inloggen</button>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ══ LOGGED IN DASHBOARD ══ -->
  <div v-else class="min-h-screen bg-[#f6f7f9] py-10">
    <div class="max-w-screen-xl mx-auto px-6">

      <!-- Breadcrumb -->
      <nav class="flex items-center gap-2 text-[0.82rem] text-[#999] mb-8">
        <router-link to="/" class="hover:text-[#111] transition-colors">
          <i class="bi bi-house"></i>
        </router-link>
        <i class="bi bi-chevron-right text-[0.7rem]"></i>
        <span class="text-[#111] font-medium">Mijn Account</span>
      </nav>

      <!-- Header bar -->
      <div class="flex items-center justify-between mb-8">
        <div>
          <h1 class="text-[1.6rem] font-bold text-[#111]">Hallo, {{ auth.userName || auth.currentUser?.email }}! 👋</h1>
          <p class="text-[0.875rem] text-[#888] mt-1">{{ auth.currentUser?.email }}</p>
        </div>
        <button @click="handleLogout"
          class="flex items-center gap-2 text-[0.85rem] text-[#888] hover:text-red-500 border border-[#e5e7eb] hover:border-red-300 rounded-lg px-4 py-2 transition-colors">
          <i class="bi bi-box-arrow-right"></i> Uitloggen
        </button>
      </div>

      <!-- Tab nav -->
      <div class="flex gap-1 border-b border-[#e5e7eb] mb-8">
        <button v-for="tab in dashTabs" :key="tab.id" @click="dashTab = tab.id"
          :class="['px-5 py-3 text-[0.875rem] font-semibold border-b-2 acct-tab -mb-px',
            dashTab === tab.id ? 'border-[#FFD500] text-[#111]' : 'border-transparent text-[#888] hover:text-[#333]']">
          <i :class="['bi mr-1.5', tab.icon]"></i>{{ tab.label }}
        </button>
      </div>

      <!-- Overzicht -->
      <div v-if="dashTab === 'overview'">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div class="bg-white rounded-2xl border border-[#e9eaec] p-6 flex items-center gap-4">
            <div class="w-12 h-12 rounded-xl bg-[#fffbe6] flex items-center justify-center">
              <i class="bi bi-box-seam text-[1.25rem] text-[#FFD500]"></i>
            </div>
            <div>
              <p class="text-[0.78rem] text-[#888]">Bestellingen</p>
              <p class="text-[1.5rem] font-bold text-[#111]">{{ orders.length }}</p>
            </div>
          </div>
          <div class="bg-white rounded-2xl border border-[#e9eaec] p-6 flex items-center gap-4">
            <div class="w-12 h-12 rounded-xl bg-[#f0fdf4] flex items-center justify-center">
              <i class="bi bi-shield-check text-[1.25rem] text-green-500"></i>
            </div>
            <div>
              <p class="text-[0.78rem] text-[#888]">Garantie actief</p>
              <p class="text-[1.5rem] font-bold text-[#111]">2 jaar</p>
            </div>
          </div>
          <div class="bg-white rounded-2xl border border-[#e9eaec] p-6 flex items-center gap-4">
            <div class="w-12 h-12 rounded-xl bg-[#f0f9ff] flex items-center justify-center">
              <i class="bi bi-person-check text-[1.25rem] text-blue-500"></i>
            </div>
            <div>
              <p class="text-[0.78rem] text-[#888]">Lid sinds</p>
              <p class="text-[1rem] font-bold text-[#111]">{{ memberSince }}</p>
            </div>
          </div>
        </div>

        <!-- Recente bestellingen preview -->
        <div class="bg-white rounded-2xl border border-[#e9eaec] p-6">
          <div class="flex items-center justify-between mb-5">
            <h2 class="text-[1rem] font-bold text-[#111]">Recente bestellingen</h2>
            <button @click="dashTab = 'orders'" class="text-[0.8rem] text-[#FFD500] font-semibold hover:underline">Alles bekijken →</button>
          </div>
          <div v-if="ordersLoading" class="text-center py-8 text-[#888]">
            <i class="bi bi-arrow-repeat spin text-[#FFD500] text-xl"></i>
          </div>
          <div v-else-if="orders.length === 0" class="text-center py-8">
            <i class="bi bi-bag text-[2rem] text-[#ddd] block mb-2"></i>
            <p class="text-[0.875rem] text-[#888]">Nog geen bestellingen</p>
            <router-link to="/producten" class="inline-block mt-3 bg-[#FFD500] text-[#111] font-semibold text-[0.85rem] px-5 py-2 rounded-lg hover:bg-[#ffb800] transition-colors">Bekijk producten</router-link>
          </div>
          <div v-else class="flex flex-col gap-3">
            <div v-for="order in orders.slice(0, 3)" :key="order.id"
              class="flex items-center justify-between p-4 rounded-xl bg-[#f9f9f9] hover:bg-[#f3f3f3] transition-colors">
              <div>
                <p class="text-[0.875rem] font-semibold text-[#111]">Bestelling #{{ order.number }}</p>
                <p class="text-[0.78rem] text-[#888] mt-0.5">{{ formatDate(order.date) }} · {{ order.items.length }} product{{ order.items.length !== 1 ? 'en' : '' }}</p>
              </div>
              <div class="flex items-center gap-3">
                <span :class="['text-[0.72rem] font-semibold px-2.5 py-1 rounded-full', statusClass(order.status)]">{{ statusLabel(order.status) }}</span>
                <span class="text-[0.875rem] font-bold text-[#111]">{{ formatPrice(order.total) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bestellingen -->
      <div v-if="dashTab === 'orders'">
        <div class="bg-white rounded-2xl border border-[#e9eaec] p-6">
          <h2 class="text-[1rem] font-bold text-[#111] mb-5">Mijn Bestellingen</h2>
          <div v-if="ordersLoading" class="text-center py-12">
            <i class="bi bi-arrow-repeat spin text-[#FFD500] text-2xl"></i>
          </div>
          <div v-else-if="orders.length === 0" class="text-center py-12">
            <i class="bi bi-bag text-[3rem] text-[#ddd] block mb-3"></i>
            <p class="text-[#888]">Nog geen bestellingen geplaatst</p>
            <router-link to="/producten" class="inline-block mt-4 bg-[#FFD500] text-[#111] font-semibold px-6 py-2.5 rounded-lg hover:bg-[#ffb800] transition-colors">Bekijk producten</router-link>
          </div>
          <div v-else class="flex flex-col divide-y divide-[#f0f0f0]">
            <div v-for="order in orders" :key="order.id" class="py-5 first:pt-0 last:pb-0">
              <div class="flex items-start justify-between gap-4">
                <div class="flex-1">
                  <div class="flex items-center gap-3 mb-2">
                    <span class="text-[0.9rem] font-bold text-[#111]">Bestelling #{{ order.number }}</span>
                    <span :class="['text-[0.72rem] font-semibold px-2.5 py-1 rounded-full', statusClass(order.status)]">{{ statusLabel(order.status) }}</span>
                  </div>
                  <p class="text-[0.8rem] text-[#888] mb-3">{{ formatDate(order.date) }}</p>
                  <div class="flex flex-wrap gap-2">
                    <span v-for="item in order.items" :key="item.name"
                      class="text-[0.78rem] bg-[#f5f5f5] text-[#555] px-2.5 py-1 rounded-full">
                      {{ item.qty }}× {{ item.name }}
                    </span>
                  </div>
                </div>
                <div class="text-right shrink-0">
                  <p class="text-[1rem] font-bold text-[#111]">{{ formatPrice(order.total) }}</p>
                  <a v-if="order.trackingUrl" :href="order.trackingUrl" target="_blank"
                    class="inline-flex items-center gap-1 mt-2 text-[0.78rem] text-blue-500 hover:underline">
                    <i class="bi bi-truck"></i> Track pakket
                  </a>
                  <a :href="`/api/invoice/${order.id}`" target="_blank"
                    class="inline-flex items-center gap-1 mt-1 text-[0.78rem] text-[#888] hover:text-[#333] hover:underline">
                    <i class="bi bi-file-earmark-pdf"></i> Factuur
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Profiel -->
      <div v-if="dashTab === 'profile'">
        <div class="max-w-xl bg-white rounded-2xl border border-[#e9eaec] p-8">
          <h2 class="text-[1rem] font-bold text-[#111] mb-6">Profiel bewerken</h2>
          <form @submit.prevent="handleProfileSave" class="flex flex-col gap-5">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-[0.82rem] font-semibold text-[#444] mb-1.5">Voornaam</label>
                <input v-model="profileForm.firstName" type="text" required
                  class="w-full px-4 py-2.5 border border-[#e5e7eb] rounded-lg text-[0.9rem] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-colors">
              </div>
              <div>
                <label class="block text-[0.82rem] font-semibold text-[#444] mb-1.5">Achternaam</label>
                <input v-model="profileForm.lastName" type="text"
                  class="w-full px-4 py-2.5 border border-[#e5e7eb] rounded-lg text-[0.9rem] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-colors">
              </div>
            </div>
            <div>
              <label class="block text-[0.82rem] font-semibold text-[#444] mb-1.5">E-mailadres</label>
              <div class="relative">
                <i class="bi bi-envelope absolute left-3.5 top-1/2 -translate-y-1/2 text-[#bbb]"></i>
                <input v-model="profileForm.email" type="email" required
                  class="w-full pl-10 pr-4 py-2.5 border border-[#e5e7eb] rounded-lg text-[0.9rem] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-colors">
              </div>
            </div>

            <div v-if="profileSuccess" class="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 text-[0.85rem]">
              <i class="bi bi-check-circle shrink-0"></i> Profiel bijgewerkt!
            </div>
            <div v-if="profileError" class="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-[0.85rem]">
              <i class="bi bi-exclamation-triangle shrink-0"></i> {{ profileError }}
            </div>

            <button type="submit" :disabled="auth.loading"
              class="bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold py-3 rounded-lg acct-btn flex items-center justify-center gap-2 disabled:opacity-60">
              <i :class="['bi', auth.loading ? 'bi-arrow-repeat spin' : 'bi-check2']"></i>
              {{ auth.loading ? 'Opslaan...' : 'Opslaan' }}
            </button>
          </form>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { formatPrice } from '@/utils/price'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@stores/auth'
import { useAnalytics } from '@composables/useAnalytics'
import Logo from '@components/Logo.vue'

const auth = useAuthStore()
const router = useRouter()
const route = useRoute()
const { trackLogin, trackSignUp } = useAnalytics()

// ── View state ('login' | 'register' | 'forgot') ─────────────────────────────
const view = ref('login')

// ── Login ─────────────────────────────────────────────────────────────────────
const showPassword = ref(false)
const loginError = ref('')
const loginForm = ref({ email: '', password: '', remember: false })

const handleLogin = async () => {
  loginError.value = ''
  try {
    await auth.login(loginForm.value.email, loginForm.value.password, loginForm.value.remember)
    trackLogin('email')
  } catch (err) {
    loginError.value = err.message || 'Inloggen mislukt. Controleer je gegevens.'
  }
}

// ── Register ──────────────────────────────────────────────────────────────────
const showRegPassword = ref(false)
const registerError = ref('')
const registerForm = ref({ firstName: '', lastName: '', email: '', password: '' })

const handleRegister = async () => {
  registerError.value = ''
  try {
    await auth.register(
      registerForm.value.firstName,
      registerForm.value.lastName,
      registerForm.value.email,
      registerForm.value.password
    )
    trackSignUp('email')
  } catch (err) {
    registerError.value = err.message || 'Registratie mislukt. Probeer het opnieuw.'
  }
}

// Password strength
const passwordStrength = computed(() => {
  const pw = registerForm.value.password
  if (!pw) return 0
  let s = 0
  if (pw.length >= 8) s++
  if (/[A-Z]/.test(pw)) s++
  if (/[0-9]/.test(pw)) s++
  if (/[^A-Za-z0-9]/.test(pw)) s++
  return s
})
const strengthColor = computed(() => (['', 'bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-green-500'])[passwordStrength.value] || 'bg-red-400')
const strengthLabel = computed(() => (['', 'Zwak', 'Matig', 'Goed', 'Sterk'])[passwordStrength.value] || '')
const pwRequirements = computed(() => {
  const pw = registerForm.value.password
  return [
    { label: 'Min. 8 tekens', met: pw.length >= 8 },
    { label: 'Hoofdletter',   met: /[A-Z]/.test(pw) },
    { label: 'Cijfer',        met: /[0-9]/.test(pw) },
    { label: 'Speciaal teken',met: /[^A-Za-z0-9]/.test(pw) },
  ]
})

// ── Forgot password ───────────────────────────────────────────────────────────
const forgotEmail = ref('')
const forgotLoading = ref(false)
const forgotError = ref('')
const forgotSent = ref(false)

const handleForgotPassword = async () => {
  forgotError.value = ''
  forgotLoading.value = true
  try {
    const res = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: forgotEmail.value })
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.message || 'Versturen mislukt')
    }
    forgotSent.value = true
  } catch (err) {
    forgotError.value = err.message
  } finally {
    forgotLoading.value = false
  }
}

// ── Left panel USPs ───────────────────────────────────────────────────────────
const usps = [
  { icon: 'bi-box-seam',     title: 'Bestellingen volgen',      sub: 'Altijd inzicht in je leveringsstatus' },
  { icon: 'bi-tag',          title: 'Installateurskorting',      sub: 'Lagere prijzen voor erkende installateurs' },
  { icon: 'bi-lightning',    title: 'Configurators',             sub: 'Bereken direct het juiste systeem' },
  { icon: 'bi-shield-check', title: '2 jaar garantie',           sub: 'Standaard op al onze producten' },
]

// ── Dashboard ─────────────────────────────────────────────────────────────────
const dashTab = ref('overview')
const dashTabs = [
  { id: 'overview', label: 'Overzicht',    icon: 'bi-grid' },
  { id: 'orders',   label: 'Bestellingen', icon: 'bi-box-seam' },
  { id: 'profile',  label: 'Profiel',      icon: 'bi-person' },
]

const orders = ref([])
const ordersLoading = ref(false)
const profileSuccess = ref(false)
const profileError = ref('')
const profileForm = ref({ firstName: '', lastName: '', email: '' })

const memberSince = computed(() => new Date().getFullYear().toString())

const loadOrders = async () => {
  ordersLoading.value = true
  orders.value = await auth.fetchOrders()
  ordersLoading.value = false
}

const handleLogout = () => {
  auth.logout()
  router.push('/')
}

const handleProfileSave = async () => {
  profileError.value = ''
  profileSuccess.value = false
  try {
    await auth.updateProfile(profileForm.value)
    profileSuccess.value = true
    setTimeout(() => profileSuccess.value = false, 3000)
  } catch (err) {
    profileError.value = err.message || 'Bijwerken mislukt'
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
}

const statusLabel = (status) => {
  const labels = {
    pending: 'In afwachting', processing: 'Verwerkt', 'on-hold': 'In de wacht',
    completed: 'Afgehandeld', cancelled: 'Geannuleerd', refunded: 'Terugbetaald',
    failed: 'Mislukt', shipped: 'Verzonden'
  }
  return labels[status] || status
}

const statusClass = (status) => {
  const classes = {
    pending:    'bg-yellow-100 text-yellow-800',
    processing: 'bg-blue-100 text-blue-800',
    'on-hold':  'bg-gray-100 text-gray-800',
    completed:  'bg-green-100 text-green-700',
    cancelled:  'bg-red-100 text-red-700',
    refunded:   'bg-purple-100 text-purple-700',
    failed:     'bg-red-100 text-red-700',
    shipped:    'bg-teal-100 text-teal-700',
  }
  return classes[status] || 'bg-gray-100 text-gray-600'
}

onMounted(() => {
  auth.init()

  // URL query param: /account?tab=register  or  /account?tab=login
  const queryTab = route.query.tab
  if (queryTab === 'register' || queryTab === 'login') {
    view.value = queryTab
  } else if (typeof window !== 'undefined') {
    const stored = sessionStorage.getItem('account_tab')
    if (stored === 'register' || stored === 'login') {
      view.value = stored
      sessionStorage.removeItem('account_tab')
    }
  }

  if (auth.isLoggedIn) {
    profileForm.value = {
      firstName: auth.currentUser?.firstName || '',
      lastName:  auth.currentUser?.lastName  || '',
      email:     auth.currentUser?.email     || '',
    }
    loadOrders()
  }
})

watch(() => auth.isLoggedIn, (loggedIn) => {
  if (loggedIn) {
    profileForm.value = {
      firstName: auth.currentUser?.firstName || '',
      lastName:  auth.currentUser?.lastName  || '',
      email:     auth.currentUser?.email     || '',
    }
    loadOrders()
  }
})

// /account/bestellingen → direct naar bestellingen-tab
watch(() => route.path, (path) => {
  if (path === '/account/bestellingen') dashTab.value = 'orders'
}, { immediate: true })
</script>

<style scoped>
.acct-btn {
  transition: background 150ms cubic-bezier(0.23, 1, 0.32, 1),
              transform 150ms cubic-bezier(0.23, 1, 0.32, 1);
}
.acct-btn:not(:disabled):hover  { transform: translateY(-1px); }
.acct-btn:not(:disabled):active { transform: scale(0.98); }
.acct-tab {
  transition: color 150ms cubic-bezier(0.23, 1, 0.32, 1),
              border-color 150ms cubic-bezier(0.23, 1, 0.32, 1);
}
@media (prefers-reduced-motion: reduce) { .acct-btn, .acct-tab { transition: none; } }
</style>
